import { useMemo, useCallback } from "react";

import { Flex } from "@chakra-ui/react";
import { groupBy, orderBy } from "lodash";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { CheckScanner } from "flows/Inventory/shared/components/CheckScanner";
import { ChecksList } from "flows/Inventory/shared/components/ChecksList";
import { ConnectionErrorModal } from "flows/Inventory/shared/components/ConnectionErrorModal";
import { TaskIsTakenModal } from "flows/Inventory/shared/components/TaskIsTakenModal";
import { useChecksList } from "flows/Inventory/shared/hooks/useChecksList";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { SwipeablePageWrapper } from "shared/components/SwipeablePageWrapper";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isNotNullNorUndefined } from "utils/tsHelpers";

// Nominal only: inventory_discrepancy_check is excluded from priority filtering by
// getTaskPriorityGroup, so this value never reaches the query.
const InboundDeviationCheckPriority = 4;
export function InboundDeviationCheckListPage() {
  const intl = useIntl();
  const navigate = useNavigate();

  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);

  const {
    rawChecks,
    refetchChecks,
    isLoadingChecks,
    isLoadingFetchNextCheckAndStartCheck,
    startCheck,
    isErrorModalOpen,
    isTaskIsTakenModalOpen,
    onCloseErrorModal,
    onCloseTaskIsTakenModal,
  } = useChecksList({
    type: [TaskType.inventory_discrepancy_check],
    origin: PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE,
  });

  const onSelectCheck = useCallback(
    (shelfId: string) => {
      if (isNotNullNorUndefined(timedActivityInstance)) {
        openOngoingActivityModal();
        return;
      }
      startCheck(shelfId, InboundDeviationCheckPriority);
    },
    [startCheck, timedActivityInstance],
  );

  const groupedChecks = useMemo(() => {
    return orderBy(
      Object.entries(groupBy(rawChecks, "shelfNumber")).map(([shelfNumber, checks]) => ({
        shelfNumber,
        type: TaskType.inventory_discrepancy_check,
        count: checks.length,
        priority: checks[0].priority,
        id: checks[0].id,
      })),
      ["shelfNumber"],
      ["asc"],
    );
  }, [rawChecks]);

  return (
    <>
      <SwipeablePageWrapper
        refetch={refetchChecks}
        isFull
        justifyContent="flex-start"
        flexDirection="column"
        h="100%"
        isLoading={isLoadingChecks}
      >
        <NavigationHeader
          title={intl.formatMessage({
            id: "pages.inventory.inbound-deviation-checks.title",
          })}
          onClickGoBack={() => navigate(routes.inventory.root)}
        />
        <Flex h="100%" direction="column" w="100%" overflowY="scroll">
          <CheckScanner
            checksList={rawChecks}
            startCheck={startCheck}
            type={[TaskType.inventory_discrepancy_check]}
            hideScannerInfo
          />
          <ChecksList items={groupedChecks} onSelectCheck={onSelectCheck} />
        </Flex>
      </SwipeablePageWrapper>
      <TaskIsTakenModal
        isOpen={isTaskIsTakenModalOpen}
        onClose={onCloseTaskIsTakenModal}
        onRefresh={refetchChecks}
      />
      <ConnectionErrorModal
        isOpen={isErrorModalOpen}
        onClose={onCloseErrorModal}
        onRefresh={refetchChecks}
      />
      <SpinnerModal isOpen={isLoadingFetchNextCheckAndStartCheck} />
    </>
  );
}
